import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ChevronUp, ChevronDown, X, AlertTriangle, RotateCcw, Eye, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useLabel } from '@/lib/labels';
import type { LabelKey } from '@/lib/label-keys';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * `/settings/report-views` (v0.4 追加要件⑪ 章⑦、D-348)。
 * シフトレポート (日別サマリ/従業員横断ビュー/36協定遵守状況) に出す列と計算式をテナントが定義する。
 * §8-46 の再現防止 (D-348 必須要件3): (a) 既定の式4本を復元できる (b) 簡易(列選択)/詳細(式編集)の2段
 * (c) AIセットアップへの導線。
 *
 * ⚠️ 詳細モードの式ビルダーは D-366 の AST 全体 (metric_ref/agg/if 等) のうち、二項演算
 * (add/sub/mul/div/eq/neq/gt/gte/lt/lte) × (メトリクス参照 or 数値リテラル) の1階層のみを
 * 対象にする意図的なスコープ縮小 — 4つの既定式 (充足率・1人あたり人時・人時客数・不足人時) は
 * すべてこの形で表現でき、複雑な入れ子や集計 (agg)・条件分岐 (if) を必要とする式は
 * 引き続き API 直叩きまたは AI セットアップ (下記リンク) 経由で作成できる。
 *
 * 設計: docs/design/v04-cross-employee-shift-report-detail.md §8-8
 */

type ViewKind = 'daily' | 'employee' | 'compliance';
const VIEW_KIND_LABELS: Record<ViewKind, string> = { daily: '日別サマリ', employee: '従業員横断ビュー', compliance: '36協定 遵守状況' };
const VIEW_KINDS: ViewKind[] = ['daily', 'employee', 'compliance'];

type ReportViewColumn = {
  column_id: string; column_kind: 'metric' | 'formula';
  metric_key: string | null; formula_id: string | null;
  label_override: string | null; sort_order: number;
};
type ReportView = {
  view_id: string; code: string; label: string; view_kind: ViewKind;
  is_default: boolean; source: string; columns: ReportViewColumn[];
};
type ReportFormula = {
  formula_id: string; code: string; label: string; grain: 'node_date' | 'person_date';
  aggregation: 'cell' | 'row_total' | 'column_total';
  expression_ast_json: string; unit: string | null; format_hint: string | null; source: string;
};
type ReportMetric = {
  metric_key: string; label_key: string; default_label: string; grain: 'node_date' | 'person_date';
  unit: string | null; sensitivity: 'normal' | 'money'; available: boolean;
};

const BINARY_OPS = ['add', 'sub', 'mul', 'div', 'eq', 'neq', 'gt', 'gte', 'lt', 'lte'] as const;
type BinaryOp = (typeof BINARY_OPS)[number];
const OP_LABELS: Record<BinaryOp, string> = {
  add: '+ 足す', sub: '− 引く', mul: '× 掛ける', div: '÷ 割る',
  eq: '= 等しい', neq: '≠ 等しくない', gt: '> より大きい', gte: '≥ 以上', lt: '< より小さい', lte: '≤ 以下',
};
const FORMAT_HINT_LABELS: Record<string, string> = { integer: '整数', decimal1: '小数第1位', percent: '割合(%)', currency: '金額' };

type Operand = { kind: 'metric'; metric_key: string } | { kind: 'literal'; value: number };

/** 二項演算のみの AST を組み立てる (詳細は上部コメント参照)。 */
const buildAst = (op: BinaryOp, left: Operand, right: Operand) => ({
  op,
  left: left.kind === 'metric' ? { op: 'metric_ref', metric_key: left.metric_key } : { op: 'literal', value: left.value },
  right: right.kind === 'metric' ? { op: 'metric_ref', metric_key: right.metric_key } : { op: 'literal', value: right.value },
});

/** 保存済み AST が本エディタの対象形 (二項 + metric_ref|literal の葉) かどうかを判定し、可能なら分解する。
 *  対象外 (agg/if/ネスト等) の場合は null を返し、呼び出し側は編集不可の生表示に倒す。 */
const decomposeAst = (json: string): { op: BinaryOp; left: Operand; right: Operand } | null => {
  try {
    const node = JSON.parse(json) as { op?: string; left?: unknown; right?: unknown };
    if (!node.op || !(BINARY_OPS as readonly string[]).includes(node.op)) return null;
    const toOperand = (raw: unknown): Operand | null => {
      const n = raw as { op?: string; metric_key?: string; value?: unknown };
      if (n?.op === 'metric_ref' && typeof n.metric_key === 'string') return { kind: 'metric', metric_key: n.metric_key };
      if (n?.op === 'literal' && typeof n.value === 'number') return { kind: 'literal', value: n.value };
      return null;
    };
    const left = toOperand(node.left);
    const right = toOperand(node.right);
    if (!left || !right) return null;
    return { op: node.op as BinaryOp, left, right };
  } catch {
    return null;
  }
};

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';

/** design-expert High指摘: `metric_key` の生値 (`node_labor_cost_amount` 等の内部識別子) を
 *  日本語主体の画面にそのまま出さない。`report_metric_catalog.label_key` はラベル辞書へ
 *  seed 済みのため `useLabel()` で解決する (label-keys.ts の LabelKey union はコンパイル時の
 *  静的リテラルのみを型付けするため、サーバー由来の動的な label_key はここでキャストする)。
 *  ⚠️ 章⑦フォローアップ (実機検証で発覚): fallback には `default_label` (label_key_catalog の
 *  既定和文ラベル) を渡すこと — `GET /labels` は tenant_labels フラグ OFF (既定) だと {} を返す
 *  (apps/web/src/lib/labels.ts) ため、fallback に metric_key の生値を渡すと未有効化テナントでは
 *  常に生値が表示されてしまっていた。 */
const metricDisplayLabel = (t: ReturnType<typeof useLabel>, m: ReportMetric): string => t(m.label_key as LabelKey, m.default_label);

function OperandEditor({ label, value, onChange, metrics, grain }: {
  label: string; value: Operand; onChange: (v: Operand) => void; metrics: ReportMetric[]; grain: 'node_date' | 'person_date';
}) {
  const t = useLabel();
  const options = metrics.filter((m) => m.grain === grain);
  return (
    <div className="min-w-0">
      <p className="text-xs text-text-sub mb-1">{label}</p>
      <div className="flex items-center gap-1.5">
        <Select value={value.kind} onValueChange={(v) => onChange(v === 'metric'
          ? { kind: 'metric', metric_key: options[0]?.metric_key ?? '' }
          : { kind: 'literal', value: 0 })}>
          <SelectTrigger className="w-28 shrink-0" aria-label={`${label}: 種類`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="metric">メトリクス</SelectItem>
            <SelectItem value="literal">数値</SelectItem>
          </SelectContent>
        </Select>
        {value.kind === 'metric' ? (
          <Select value={value.metric_key} onValueChange={(mk) => onChange({ kind: 'metric', metric_key: mk })}>
            <SelectTrigger className="min-w-0 flex-1" aria-label={`${label}: メトリクス`}><SelectValue placeholder="選択してください" /></SelectTrigger>
            <SelectContent>
              {options.map((m) => (
                <SelectItem key={m.metric_key} value={m.metric_key}>
                  {metricDisplayLabel(t, m)}{m.sensitivity === 'money' ? ' (金額)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <input type="number" value={value.value} onChange={(e) => onChange({ kind: 'literal', value: Number(e.target.value) })}
            className={`${inputCls} min-w-0 flex-1`} aria-label={`${label}: 数値`} />
        )}
      </div>
    </div>
  );
}

function FormulaModal({ draft, metrics, onClose, onSaved }: {
  draft: { formula_id: string | null; code: string; label: string; grain: 'node_date' | 'person_date'; aggregation: ReportFormula['aggregation']; unit: string; formatHint: string; op: BinaryOp; left: Operand; right: Operand };
  metrics: ReportMetric[]; onClose: () => void; onSaved: () => void;
}) {
  const [state, setState] = useState(draft);
  const involvesMoney = useMemo(() => {
    const keyOf = (o: Operand) => (o.kind === 'metric' ? o.metric_key : null);
    const key1 = keyOf(state.left); const key2 = keyOf(state.right);
    return metrics.some((m) => m.sensitivity === 'money' && (m.metric_key === key1 || m.metric_key === key2));
  }, [state.left, state.right, metrics]);

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        code: state.code.trim(), label: state.label.trim(), grain: state.grain, aggregation: state.aggregation,
        expression_ast_json: JSON.stringify(buildAst(state.op, state.left, state.right)),
        unit: state.unit.trim() || null, format_hint: state.formatHint || null,
      };
      return state.formula_id
        ? apiFetch(`/report-formulas/${state.formula_id}`, { method: 'PUT', body: JSON.stringify({ label: payload.label, aggregation: payload.aggregation, expression_ast_json: payload.expression_ast_json, unit: payload.unit, format_hint: payload.format_hint }) })
        : apiFetch('/report-formulas', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { onSaved(); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const canSave = state.code.trim().length > 0 && state.label.trim().length > 0
    && (state.left.kind !== 'metric' || !!state.left.metric_key) && (state.right.kind !== 'metric' || !!state.right.metric_key);

  return (
    <Modal open title={state.formula_id ? '式を編集' : '式を新規作成'} onClose={onClose} size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
        </>
      )}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-text-sub mb-1">コード (半角英数字、一意)</label>
          <input value={state.code} disabled={!!state.formula_id} onChange={(e) => setState({ ...state, code: e.target.value })}
            maxLength={64} className={`${inputCls} disabled:opacity-60`} placeholder="例: fulfillment_rate" />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">ラベル</label>
          <input value={state.label} onChange={(e) => setState({ ...state, label: e.target.value })} maxLength={100} className={inputCls} placeholder="例: 充足率" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-sub mb-1">対象 (店舗別/従業員別)</label>
            <Select value={state.grain} onValueChange={(v) => setState({ ...state, grain: v as 'node_date' | 'person_date', left: { kind: 'literal', value: 0 }, right: { kind: 'literal', value: 0 } })} disabled={!!state.formula_id}>
              <SelectTrigger aria-label="対象" className="disabled:opacity-60"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="node_date">店舗×日付</SelectItem>
                <SelectItem value="person_date">従業員×日付</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">表示形式</label>
            <Select value={state.formatHint || '__none__'} onValueChange={(v) => setState({ ...state, formatHint: v === '__none__' ? '' : v })}>
              <SelectTrigger aria-label="表示形式"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">指定なし</SelectItem>
                {Object.entries(FORMAT_HINT_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="rounded border border-surface-border p-3 space-y-3 bg-surface-sunken">
          <p className="text-xs font-semibold text-text-sub">計算式 (メトリクス または 数値 を演算子でつなぐ)</p>
          <OperandEditor label="左辺" value={state.left} onChange={(v) => setState({ ...state, left: v })} metrics={metrics} grain={state.grain} />
          <div>
            <p className="text-xs text-text-sub mb-1">演算子</p>
            <Select value={state.op} onValueChange={(v) => setState({ ...state, op: v as BinaryOp })}>
              <SelectTrigger aria-label="演算子"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BINARY_OPS.map((op) => <SelectItem key={op} value={op}>{OP_LABELS[op]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <OperandEditor label="右辺" value={state.right} onChange={(v) => setState({ ...state, right: v })} metrics={metrics} grain={state.grain} />
        </div>
        {involvesMoney && (
          <p className="text-xs text-alert-text flex items-start gap-1.5">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden />
            この式は金額メトリクスを含むため、閲覧者や店舗の設定によっては値が表示されません (大半の閲覧者には非表示になります)。
          </p>
        )}
      </div>
    </Modal>
  );
}

function PreviewPanel({ formula, onClose }: { formula: ReportFormula; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['report-formula-preview', formula.formula_id],
    queryFn: () => apiFetch<{
      range: { from: string; to: string }; grain: string; available: boolean; omitted_reason: string | null;
      scope_summary: { node_count: number; target_count: number };
      results: Array<{ target_key: string; value: number | string | boolean | null; error_code: string | null }>;
    }>('/report-formulas/preview', { method: 'POST', body: JSON.stringify({ formula_id: formula.formula_id }) }),
  });

  return (
    <Modal open title={`プレビュー: ${formula.label}`} onClose={onClose} size="md" footer={<Button variant="secondary" onClick={onClose}>閉じる</Button>}>
      {isLoading ? (
        <p className="text-sm text-text-sub">読み込み中…</p>
      ) : error ? (
        <p className="text-sm text-danger-text">{error instanceof ApiClientError ? error.message : '取得に失敗しました'}</p>
      ) : !data?.available ? (
        <EmptyState message="現在この式の結果は表示できません" description="権限・粒度設定・可視性ルールのいずれかにより値が非公開になっています。" />
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-text-sub">
            期間: {data.range.from}〜{data.range.to} / 対象: {data.scope_summary.node_count}店舗・{data.scope_summary.target_count}件
          </p>
          <div className="overflow-x-auto max-h-80 border border-surface-border rounded">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken sticky top-0">
                <tr><th scope="col" className="text-left px-2 py-1.5">対象</th><th scope="col" className="text-right px-2 py-1.5">値</th></tr>
              </thead>
              <tbody>
                {data.results.length === 0 ? (
                  <tr><td colSpan={2} className="px-2 py-3 text-center text-text-sub">直近7日間に該当データがありません</td></tr>
                ) : data.results.map((r) => (
                  <tr key={r.target_key} className="border-t border-surface-border">
                    <td className="px-2 py-1.5 break-all">{r.target_key}</td>
                    <td className="px-2 py-1.5 text-right">{r.value === null ? (r.error_code ? '—' : '(空)') : String(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

function FormulaManager({ formulas, metrics, onChanged }: { formulas: ReportFormula[]; metrics: ReportMetric[]; onChanged: () => void }) {
  const [modalDraft, setModalDraft] = useState<Parameters<typeof FormulaModal>[0]['draft'] | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReportFormula | null>(null);
  const [previewTarget, setPreviewTarget] = useState<ReportFormula | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/report-formulas/${id}`, { method: 'DELETE' }),
    onSuccess: () => { onChanged(); toast.success('削除しました'); setDeleteTarget(null); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });
  const restoreMut = useMutation({
    mutationFn: () => apiFetch<{ restored: string[] }>('/report-formulas/restore-presets', { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: (data) => {
      onChanged();
      toast.success(data.restored.length > 0 ? `既定の式を${data.restored.length}件復元しました` : '既定の式はすべて揃っています');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '復元に失敗しました'),
  });

  const openCreate = () => setModalDraft({
    formula_id: null, code: '', label: '', grain: 'node_date', aggregation: 'cell', unit: '', formatHint: '',
    op: 'div', left: { kind: 'literal', value: 0 }, right: { kind: 'literal', value: 0 },
  });
  const openEdit = (f: ReportFormula) => {
    const decomposed = decomposeAst(f.expression_ast_json);
    if (!decomposed) {
      toast.error('この式はこの画面では編集できません (集計や条件分岐など高度な構成のため)。AIセットアップまたはAPI経由で編集してください。');
      return;
    }
    setModalDraft({
      formula_id: f.formula_id, code: f.code, label: f.label, grain: f.grain, aggregation: f.aggregation,
      unit: f.unit ?? '', formatHint: f.format_hint ?? '', op: decomposed.op, left: decomposed.left, right: decomposed.right,
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-text-sub">列に追加できる計算式を管理します。式は「日別サマリ」「従業員横断ビュー」共通のテナント全体の一覧です。</p>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" leftIcon={<RotateCcw size={14} />} loading={restoreMut.isPending} onClick={() => restoreMut.mutate()}>既定の式を復元する</Button>
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={openCreate}>式を新規作成</Button>
        </div>
      </div>
      {formulas.length === 0 ? (
        <EmptyState message="式がまだありません" description="「式を新規作成」から作成するか、「既定の式を復元する」で標準の4式を投入してください。" />
      ) : (
        <ul className="space-y-2">
          {formulas.map((f) => (
            <li key={f.formula_id} className="flex items-center justify-between gap-2 flex-wrap border border-surface-border rounded-lg bg-white p-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text-body break-words">
                  {f.label}
                  {f.source === 'preset' && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border align-middle">既定</span>}
                  {f.source === 'ai_setup' && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border align-middle">AI提案</span>}
                </p>
                <p className="text-xs text-text-sub mt-0.5">code: {f.code} / {f.grain === 'node_date' ? '店舗×日付' : '従業員×日付'}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" leftIcon={<Eye size={14} />} onClick={() => setPreviewTarget(f)}>プレビュー</Button>
                <Button variant="ghost" size="sm" onClick={() => openEdit(f)}>編集</Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(f)}>削除</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {modalDraft && <FormulaModal draft={modalDraft} metrics={metrics} onClose={() => setModalDraft(null)} onSaved={() => { onChanged(); setModalDraft(null); }} />}
      {previewTarget && <PreviewPanel formula={previewTarget} onClose={() => setPreviewTarget(null)} />}
      <ConfirmDialog
        open={!!deleteTarget} title="式を削除しますか" destructive
        message={`「${deleteTarget?.label}」を削除します。この式が列として使われているビューがある場合は削除できません。`}
        loading={deleteMut.isPending}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.formula_id)}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function ColumnEditor({ view, formulas, metrics, onChanged }: { view: ReportView; formulas: ReportFormula[]; metrics: ReportMetric[]; onChanged: () => void }) {
  const t = useLabel();
  const [columns, setColumns] = useState<ReportViewColumn[]>(view.columns);
  const [dirty, setDirty] = useState(false);
  const grain = view.view_kind === 'employee' ? 'person_date' : 'node_date';

  const metricByKey = useMemo(() => new Map(metrics.map((m) => [m.metric_key, m])), [metrics]);
  const formulaById = useMemo(() => new Map(formulas.map((f) => [f.formula_id, f])), [formulas]);
  const labelOf = (c: ReportViewColumn) => c.label_override
    || (c.column_kind === 'metric'
      ? (c.metric_key ? (metricByKey.get(c.metric_key) ? metricDisplayLabel(t, metricByKey.get(c.metric_key)!) : c.metric_key) : '')
      : (c.formula_id ? formulaById.get(c.formula_id)?.label ?? c.formula_id : ''));

  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/report-views/${view.view_id}/columns`, {
      method: 'PUT',
      body: JSON.stringify({ columns: columns.map((c) => ({ column_kind: c.column_kind, metric_key: c.metric_key, formula_id: c.formula_id, label_override: c.label_override })) }),
    }),
    onSuccess: () => { onChanged(); setDirty(false); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= columns.length) return;
    const next = [...columns];
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    setColumns(next);
    setDirty(true);
  };
  const remove = (idx: number) => { setColumns(columns.filter((_, i) => i !== idx)); setDirty(true); };
  const [addKind, setAddKind] = useState<'metric' | 'formula'>('metric');
  const [addValue, setAddValue] = useState('');
  const addColumn = () => {
    if (!addValue) return;
    setColumns([...columns, {
      column_id: `draft-${columns.length}-${addValue}`, column_kind: addKind,
      metric_key: addKind === 'metric' ? addValue : null, formula_id: addKind === 'formula' ? addValue : null,
      label_override: null, sort_order: columns.length,
    }]);
    setAddValue('');
    setDirty(true);
  };

  const availableMetrics = metrics.filter((m) => m.grain === grain);
  const availableFormulas = formulas.filter((f) => f.grain === grain);

  return (
    <div className="space-y-3">
      <p className="text-xs text-text-sub">
        「{VIEW_KIND_LABELS[view.view_kind]}」に表示する列と並び順を選びます。列を1つも設定しない場合は既定の全項目が表示されます。
      </p>
      {columns.length === 0 ? (
        <EmptyState message="列が設定されていません" description="下から列を追加するか、未設定のままにすると既定の全項目が表示されます。" />
      ) : (
        <ul className="space-y-1.5">
          {columns.map((c, i) => (
            <li key={c.column_id} className="flex items-center justify-between gap-2 border border-surface-border rounded-lg bg-white px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex flex-col shrink-0">
                  <button type="button" aria-label="上へ" disabled={i === 0} onClick={() => move(i, -1)} className="w-11 h-11 sm:w-8 sm:h-6 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronUp size={14} /></button>
                  <button type="button" aria-label="下へ" disabled={i === columns.length - 1} onClick={() => move(i, 1)} className="w-11 h-11 sm:w-8 sm:h-6 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronDown size={14} /></button>
                </div>
                <p className="text-sm text-text-body break-words min-w-0">
                  {labelOf(c)}
                  <span className="ml-1.5 text-[11px] text-text-sub">{c.column_kind === 'metric' ? 'メトリクス' : '式'}</span>
                </p>
              </div>
              <button type="button" aria-label="この列を削除" onClick={() => remove(i)} className="w-11 h-11 sm:w-8 sm:h-8 shrink-0 inline-flex items-center justify-center text-text-sub hover:text-danger-text"><X size={16} /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2 flex-wrap border-t border-surface-border pt-3">
        <Select value={addKind} onValueChange={(v) => { setAddKind(v as 'metric' | 'formula'); setAddValue(''); }}>
          <SelectTrigger className="w-32 shrink-0" aria-label="追加する種類"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="metric">メトリクス</SelectItem><SelectItem value="formula">式</SelectItem></SelectContent>
        </Select>
        <Select value={addValue} onValueChange={setAddValue}>
          <SelectTrigger className="min-w-0 flex-1 sm:w-64" aria-label="追加する項目"><SelectValue placeholder="項目を選択" /></SelectTrigger>
          <SelectContent>
            {(addKind === 'metric' ? availableMetrics.map((m) => ({ value: m.metric_key, label: `${metricDisplayLabel(t, m)}${m.sensitivity === 'money' ? ' (金額)' : ''}` }))
              : availableFormulas.map((f) => ({ value: f.formula_id, label: f.label })))
              .map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} disabled={!addValue} onClick={addColumn}>列を追加</Button>
      </div>
      <div className="flex justify-end">
        <Button variant="primary" size="sm" disabled={!dirty || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>並び順を保存</Button>
      </div>
    </div>
  );
}

export function ReportViewsSettingsPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const [viewKind, setViewKind] = useState<ViewKind>('daily');
  const [mode, setMode] = useState<'simple' | 'detail'>('simple');

  const { data: viewsData, isLoading: viewsLoading } = useQuery({
    queryKey: ['report-views'],
    queryFn: () => apiFetch<{ views: ReportView[] }>('/report-views'),
  });
  const { data: formulasData } = useQuery({
    queryKey: ['report-formulas'],
    queryFn: () => apiFetch<{ formulas: ReportFormula[] }>('/report-formulas'),
  });
  const { data: metricsData } = useQuery({
    queryKey: ['report-metrics'],
    queryFn: () => apiFetch<{ metrics: ReportMetric[] }>('/report-metrics'),
  });

  const onChanged = () => {
    qc.invalidateQueries({ queryKey: ['report-views'] });
    qc.invalidateQueries({ queryKey: ['report-formulas'] });
  };

  // platform-reviewer Minor指摘: 既定ビューの復元手段が formulas 側にしか無かった (新規テナントは
  // migration の per-tenant backfill を受けられないため0件のまま)。
  const restoreViewsMut = useMutation({
    mutationFn: () => apiFetch<{ restored: string[] }>('/report-views/restore-presets', { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: (data) => {
      onChanged();
      toast.success(data.restored.length > 0 ? '既定のビューを復元しました' : '既定のビューは既に揃っています');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '復元に失敗しました'),
  });

  const currentView = viewsData?.views.find((v) => v.view_kind === viewKind);
  const formulas = formulasData?.formulas ?? [];
  const metrics = metricsData?.metrics ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">{t('entity.report_views', 'レポート設定 (表示項目・計算式)')}</h2>
        <p className="text-sm text-text-sub mt-1">
          日別サマリ・従業員横断ビューに表示する列と、そこで使う計算式を設定します。何も設定しない場合は既定の全項目が表示されます。
        </p>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="対象画面">
        {VIEW_KINDS.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={viewKind === k} onClick={() => setViewKind(k)}
            className={`min-h-[44px] px-3 py-2 rounded-full text-sm font-medium border ${viewKind === k ? 'bg-misespark-navy text-white border-misespark-navy' : 'bg-white text-text-body border-surface-border hover:bg-surface-sunken'}`}>
            {VIEW_KIND_LABELS[k]}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 border border-surface-border rounded-full w-fit p-1 bg-surface-sunken">
        <button type="button" aria-pressed={mode === 'simple'} onClick={() => setMode('simple')}
          className={`min-h-[40px] px-3 rounded-full text-xs font-semibold ${mode === 'simple' ? 'bg-white shadow-sm text-text-body' : 'text-text-sub'}`}>
          {t('field.report_views.simple_mode', '簡易モード')}
        </button>
        <button type="button" aria-pressed={mode === 'detail'} onClick={() => setMode('detail')}
          className={`min-h-[40px] px-3 rounded-full text-xs font-semibold ${mode === 'detail' ? 'bg-white shadow-sm text-text-body' : 'text-text-sub'}`}>
          {t('field.report_views.detail_mode', '詳細モード (式エディタ)')}
        </button>
        <Link to="/settings/ai-setup" className="ml-2 flex items-center gap-1 min-h-[40px] px-3 rounded-full text-xs font-semibold text-misespark-navy hover:bg-white">
          <Sparkles size={14} aria-hidden /> AIに相談して式を作る
        </Link>
      </div>

      {viewsLoading ? (
        <p className="text-sm text-text-sub">読み込み中…</p>
      ) : mode === 'simple' ? (
        currentView ? (
          // ⚠️ design-and-code HIGH指摘 (§8-95型): key を付けないとタブ切替時に ColumnEditor の
          // インスタンスが再利用され、内部の columns state が前タブの値のまま残る。この状態で
          // 「並び順を保存」を押すと無関係なビューへ古い列配列を上書き保存してしまう。
          <ColumnEditor key={currentView.view_id} view={currentView} formulas={formulas} metrics={metrics} onChanged={onChanged} />
        ) : (
          <EmptyState
            message="このレポートのビューがまだありません" description="通常はレポート設定機能を有効化した際に自動的に作成されますが、見当たらない場合は既定のビューを復元してください。"
            actionLabel={restoreViewsMut.isPending ? '復元中…' : '既定のビューを復元する'} onAction={() => restoreViewsMut.mutate()}
          />
        )
      ) : (
        <FormulaManager formulas={formulas} metrics={metrics} onChanged={onChanged} />
      )}
    </div>
  );
}
